import { useEffect, useMemo, useState } from 'react';
import { decodeCurrency, formatNumber, shortAddress } from '../lib/format.js';
import { DenominationSummary } from './DenominationSummary.jsx';

const PAGE_SIZE = 50;
const SKELETON_ROWS = 6;
const CONTROL_CHIP_KEYS = ['clawback', 'requireAuth', 'globalFreeze', 'locked', 'transferFee', 'issuerOnly'];

const columns = [
  { key: 'name', label: 'Asset / program' },
  { key: 'identity', label: 'Ledger identity', sortable: false },
  { key: 'balance', label: 'Observed balance', numeric: true },
  { key: 'evidence', label: 'Evidence' },
];

function balanceLabel(row) {
  if (row.availability === 'legacy' || row.availability === 'pilot') return 'Residual obligation';
  if (row.source === 'xrplmeta') return 'Indexer-reported supply';
  if (row.excluded > 0) return 'Non-treasury distribution';
  return row.kind === 'mpt' ? 'Outstanding amount' : 'Issuer obligation';
}

function evidenceLabel(row) {
  if (row.source === 'xrplmeta') return 'Metadata';
  if (row.verification === 'verified') return 'Verified';
  return 'Corroborated';
}

function identityCode(row) {
  if (row.kind === 'mpt') return { code: shortAddress(row.issuanceId, 9, 7), full: row.issuanceId, sub: 'Multi-purpose token' };
  const decoded = decodeCurrency(row.currency);
  const isHex = /^[0-9A-F]{40}$/i.test(row.currency);
  return {
    code: shortAddress(row.issuer, 7, 6),
    full: row.issuer,
    sub: isHex ? `${decoded} (160-bit code)` : decoded,
    subTitle: isHex ? row.currency : undefined,
  };
}

function csvCell(value) {
  const text = String(value ?? '');
  const safe = /^[=+\-@]/.test(text.trimStart()) ? `'${text}` : text;
  return `"${safe.replaceAll('"', '""')}"`;
}

function SkeletonRows() {
  return Array.from({ length: SKELETON_ROWS }, (_, index) => (
    <tr className="skeleton-row" key={index} aria-hidden="true">
      <td><span className="skeleton" style={{ width: '38%' }} /><span className="skeleton" style={{ width: '62%' }} /></td>
      <td><span className="skeleton" style={{ width: '55%' }} /><span className="skeleton" style={{ width: '30%' }} /></td>
      <td><span className="skeleton" style={{ width: '44%' }} /><span className="skeleton" style={{ width: '36%' }} /></td>
      <td><span className="skeleton" style={{ width: '32%' }} /><span className="skeleton" style={{ width: '48%' }} /></td>
    </tr>
  ));
}

export function SupplyTable({ supplies, ledgerIndex, snapshotAt, onSelect, loading }) {
  const [query, setQuery] = useState('');
  const [evidence, setEvidence] = useState('all');
  const [sort, setSort] = useState({ key: 'balance', direction: 'desc' });
  const [hideZero, setHideZero] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const rows = supplies.filter((row) => {
      if (hideZero && Number(row.circulating) === 0) return false;
      if (evidence !== 'all' && (row.source === 'xrplmeta' ? 'metadata' : row.verification) !== evidence) return false;
      if (!needle) return true;
      return [
        row.ticker,
        row.programName,
        row.issuer,
        row.currency,
        decodeCurrency(row.currency),
        row.issuanceId,
        row.unit,
      ].filter(Boolean).some((value) => String(value).toLowerCase().includes(needle));
    });

    const sign = sort.direction === 'asc' ? 1 : -1;
    return rows.sort((a, b) => {
      if (sort.key === 'name') return sign * (a.programName.localeCompare(b.programName) || a.ticker.localeCompare(b.ticker));
      if (sort.key === 'evidence') return sign * (evidenceLabel(a).localeCompare(evidenceLabel(b)) || a.programName.localeCompare(b.programName));
      return sign * (Number(a.circulating || 0) - Number(b.circulating || 0)) || a.programName.localeCompare(b.programName);
    });
  }, [evidence, hideZero, query, sort, supplies]);

  useEffect(() => setVisibleCount(PAGE_SIZE), [evidence, hideZero, query, sort]);

  const toggleSort = (key) => {
    setSort((current) => {
      if (current.key !== key) return { key, direction: key === 'balance' ? 'desc' : 'asc' };
      return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    });
  };

  const exportCsv = () => {
    const headers = ['Asset', 'Program', 'Kind', 'Issuer', 'Currency code', 'Decoded currency', 'Issuance ID', 'Observed balance', 'Unit', 'Balance definition', 'Evidence', 'Issuer controls', 'Ledger'];
    const rows = filtered.map((row) => [
      row.ticker,
      row.programName,
      row.kind,
      row.issuer,
      row.kind === 'mpt' ? '' : row.currency,
      row.kind === 'mpt' ? '' : decodeCurrency(row.currency),
      row.kind === 'mpt' ? row.issuanceId : '',
      row.circulating,
      row.unit,
      balanceLabel(row),
      evidenceLabel(row),
      (row.controls?.controls || []).map((control) => control.label).join('; '),
      row.ledgerIndex,
    ]);
    const csv = [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `assetglobe-ledger-state-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const showSkeleton = loading && !supplies.length;

  return (
    <section className="supply-panel panel" aria-labelledby="supply-title">
      <div className="supply-header">
        <div>
          <span className="eyebrow">Direct state</span>
          <h2 id="supply-title">On-ledger instruments</h2>
          <p>Token units observed on XRPL. This is not a USD market-cap estimate.</p>
        </div>
        <div className="snapshot-meta">
          <span>{supplies.length} identities</span>
          <span>{ledgerIndex ? `Ledger ${Number(ledgerIndex).toLocaleString()}` : 'Awaiting ledger'}</span>
          {snapshotAt && <time dateTime={snapshotAt}>Snapshot {new Date(snapshotAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</time>}
        </div>
      </div>

      <DenominationSummary supplies={supplies} onSelect={onSelect} />

      <div className="supply-controls">
        <label className="search-field supply-search">
          <span className="sr-only">Search on-ledger instruments</span>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m21 21-4.4-4.4m2.4-5.1a7.5 7.5 0 1 1-15 0 7.5 7.5 0 0 1 15 0Z" /></svg>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search asset, issuer or currency" />
        </label>
        <label>
          <span className="sr-only">Filter by evidence</span>
          <select value={evidence} onChange={(event) => setEvidence(event.target.value)}>
            <option value="all">All evidence</option>
            <option value="verified">Verified</option>
            <option value="corroborated">Corroborated</option>
            <option value="metadata">Metadata</option>
          </select>
        </label>
        <label className="sort-select">
          <span className="sr-only">Sort instruments</span>
          <select value={`${sort.key}:${sort.direction}`} onChange={(event) => { const [key, direction] = event.target.value.split(':'); setSort({ key, direction }); }}>
            <option value="balance:desc">Largest balance</option>
            <option value="balance:asc">Smallest balance</option>
            <option value="name:asc">Program name A to Z</option>
            <option value="evidence:asc">Evidence level</option>
          </select>
        </label>
        <label className="check-control"><input type="checkbox" checked={hideZero} onChange={(event) => setHideZero(event.target.checked)} /> Hide zero</label>
        <button className="secondary-button" onClick={exportCsv} disabled={!filtered.length}>Export CSV</button>
      </div>

      <div className="supply-results-meta"><strong>{filtered.length}</strong> matching identities</div>
      <div className="supply-table-wrap" aria-busy={loading}>
        <table className="supply-table">
          <thead>
            <tr>
              {columns.map((column) => {
                const active = sort.key === column.key;
                const sortable = column.sortable !== false;
                return (
                  <th key={column.key} aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
                    {sortable ? (
                      <button type="button" className={`sort-header ${active ? 'active' : ''}`} onClick={() => toggleSort(column.key)}>
                        {column.label}
                        <span aria-hidden="true">{active ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
                      </button>
                    ) : column.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {showSkeleton && <SkeletonRows />}
            {filtered.slice(0, visibleCount).map((row) => {
              const identity = identityCode(row);
              const chips = (row.controls?.controls || []).filter((control) => CONTROL_CHIP_KEYS.includes(control.key));
              return (
                <tr key={row.key}>
                  <td data-label="Asset">
                    <button className="table-asset-button" onClick={() => onSelect(row.programId)}>
                      <strong>{row.ticker}</strong>
                      <span>{row.programName}</span>
                    </button>
                  </td>
                  <td className="supply-identity" data-label="Ledger identity">
                    <code title={identity.full}>{identity.code}</code>
                    <span title={identity.subTitle}>{identity.sub}</span>
                  </td>
                  <td className="supply-balance" data-label="Observed balance">
                    <strong>{formatNumber(row.circulating)} {row.unit}</strong>
                    <span>{balanceLabel(row)}</span>
                    {row.excluded > 0 && <span>{formatNumber(row.gross)} gross · {formatNumber(row.excluded)} treasury</span>}
                  </td>
                  <td data-label="Evidence">
                    <span className={`table-evidence verification-${row.verification}`}>{evidenceLabel(row)}</span>
                    <span>{row.source === 'xrplmeta' ? `${row.holders || 0} holder${row.holders === 1 ? '' : 's'}` : row.ledgerIndex ? `L ${Number(row.ledgerIndex).toLocaleString()}` : 'Pending'}</span>
                    {!!chips.length && (
                      <span className="control-chips" aria-label="Issuer controls">
                        {chips.map((control) => <i key={control.key} className={`control-chip tone-${control.tone}`}>{control.label}</i>)}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {showSkeleton && <div className="table-loading"><span />Reading issuer obligations and MPT entries from a validated ledger</div>}
        {!loading && !filtered.length && <div className="empty-state">No on-ledger identities match these filters.</div>}
      </div>
      {visibleCount < filtered.length && (
        <button className="load-more" onClick={() => setVisibleCount((value) => value + PAGE_SIZE)}>
          Show {Math.min(PAGE_SIZE, filtered.length - visibleCount)} more <span>{visibleCount} of {filtered.length}</span>
        </button>
      )}
    </section>
  );
}
